import React from 'react';
import { useNavigate } from 'react-router';
import { ObjectStates as ObjectTypeStates } from '../../../../common/src/enums/ObjectTypeStates';
import { getRequestBodySchema } from '../../../../common/src/routes/openApiSchemaHelpers';
import { useTranslate } from '../../mixins/translate/translate';
import { Roles } from '../../utils/enums/Roles';
import {
    Content,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    Panel,
    PanelBody,
    PanelButton,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../elements';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { ValidatingJsonSchemaForm } from '../validatingComponents/ValidatingJsonSchemaForm';
import {
    ValidatingEditPanelSaveButton,
    ValidatingEditPanelSwitch,
    ValidatingFilterDropdown,
    ValidatingFilterDropdownWithUrl,
    ValidatingFormInputText,
} from '../validatingElements';

export function ObjectTypeAddEdit(props: { params: { id?: string } }) {
    const translate = useTranslate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const navigate = useNavigate();

    // eslint-disable-next-line @typescript-eslint/require-await
    async function handleSavedOrCancelled() {
        navigate('/settings/objecttypes');
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    {(props.params.id && (
                        <TopbarCrumbActive title={translate('common.editItem', translate('common.objectType'))} icon="fa-cogs" />
                    )) || <TopbarCrumbActive title={translate('common.addItem', translate('common.objectType'))} icon="fa-cogs" />}
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    <Panel small>
                        <PanelBody noPadding>
                            <ValidatingJsonSchemaForm
                                createSchema={getRequestBodySchema('/api/objectTypes', 'post')}
                                updateSchema={getRequestBodySchema('/api/objectTypes/{objectTypeId}', 'post')}
                                createUrl={'/api/objectTypes/'}
                                updateUrl={`/api/objectTypes/${props.params.id}`}
                                itemUrl={`/api/objectTypes/${props.params.id}`}
                                id={props.params.id}
                                onSubmitted={handleSavedOrCancelled}
                                defaultItem={{ createNenInspectionStandard: false }}
                            >
                                <EditPanel>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="name" topLabel={translate('common.name')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFilterDropdownWithUrl
                                                name="languageId"
                                                topLabel={translate('common.language')}
                                                placeholder={translate('common.selectItem', translate('common.language'))}
                                                url={'/api/languageList'}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    {isAdmin && (
                                        <>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>
                                                    <ValidatingFilterDropdownWithUrl
                                                        name="clientCompanyId"
                                                        topLabel={translate('common.client')}
                                                        placeholder={translate('common.allItems', translate('common.clients'))}
                                                        url={'/api/clients'}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={6}>
                                                    <ValidatingFilterDropdown
                                                        name="status"
                                                        topLabel="Status"
                                                        selectOptions={[
                                                            { value: ObjectTypeStates.Active, text: translate(`status.${ObjectTypeStates.Active}`) },
                                                            {
                                                                value: ObjectTypeStates.Deleted,
                                                                text: translate(`status.${ObjectTypeStates.Deleted}`),
                                                            },
                                                        ]}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            {!props.params.id && (
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <ValidatingEditPanelSwitch
                                                            name="createNenInspectionStandard"
                                                            topLabel={`${translate('common.inspectionCompositionStandard')} NEN2767`}
                                                            onText="On"
                                                            offText="Off"
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            )}
                                        </>
                                    )}
                                </EditPanel>
                                <EditPanelButtons footer>
                                    <PanelButton text={translate('common.cancel')} onClick={handleSavedOrCancelled} />
                                    <ValidatingEditPanelSaveButton />
                                </EditPanelButtons>
                            </ValidatingJsonSchemaForm>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
